import { Link } from 'react-router-dom';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import { useEffect, useState } from 'react';
import { supabase, type Artwork } from '../../lib/supabase';

export default function HomePage() {
  const [featuredArtworks, setFeaturedArtworks] = useState<Artwork[]>([]);
  const [recentArtworks, setRecentArtworks] = useState<Artwork[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArtworks();
  }, []);

  const fetchArtworks = async () => {
    try {
      const { data: featured } = await supabase
        .from('artworks')
        .select('*')
        .eq('status', 'published')
        .eq('featured', true)
        .limit(3);

      const { data: recent } = await supabase
        .from('artworks')
        .select('*')
        .eq('status', 'published')
        .order('created_at', { ascending: false })
        .limit(6);

      setFeaturedArtworks(featured || []);
      setRecentArtworks(recent || []);
    } catch (error) {
      console.error('Error fetching artworks:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://public.readdy.ai/ai/img_res/e836b52563f3e88b89f9ac13aff5eb1c.jpg')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white w-full">
          <h1 className="text-5xl md:text-7xl font-bold mb-6">
            Premium Digital Artwork
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-200 max-w-3xl mx-auto">
            Discover stunning design resources, templates, and digital art for your creative projects
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/shop"
              className="bg-teal-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Shop Now
            </Link>
            <Link
              to="/portfolio"
              className="bg-white text-gray-900 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              View Portfolio
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Artworks */}
      {featuredArtworks.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Featured Artworks</h2>
              <p className="text-xl text-gray-600">Handpicked designs for your next project</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-product-shop>
              {featuredArtworks.map((artwork) => (
                <Link
                  key={artwork.id}
                  to={`/artwork/${artwork.slug}`}
                  className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer"
                >
                  <div className="aspect-square overflow-hidden bg-gray-100">
                    <img
                      src={artwork.primary_image || `https://readdy.ai/api/search-image?query=professional%20graphic%20design%20artwork%20$%7Bartwork.category%7D%20modern%20clean%20simple%20background%20high%20quality%20digital%20art%20vibrant%20colors%20creative%20design&width=600&height=600&seq=${artwork.id}&orientation=squarish`}
                      alt={artwork.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                      {artwork.title}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-2">{artwork.short_description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-teal-600">
                        ${artwork.price}
                      </span>
                      <span className="text-sm text-gray-500">{artwork.formats?.join(', ')}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Recently Added */}
      {recentArtworks.length > 0 && (
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Recently Added</h2>
              <p className="text-xl text-gray-600">Fresh designs just for you</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-product-shop>
              {recentArtworks.map((artwork) => (
                <Link
                  key={artwork.id}
                  to={`/artwork/${artwork.slug}`}
                  className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer"
                >
                  <div className="aspect-video overflow-hidden bg-gray-100">
                    <img
                      src={artwork.primary_image || `https://readdy.ai/api/search-image?query=creative%20digital%20design%20$%7Bartwork.category%7D%20professional%20artwork%20modern%20style%20clean%20simple%20background%20vibrant%20colors%20high%20quality&width=800&height=450&seq=${artwork.id}r&orientation=landscape`}
                      alt={artwork.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                      {artwork.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">{artwork.short_description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xl font-bold text-teal-600">
                        ${artwork.price}
                      </span>
                      <span className="text-xs text-gray-500 uppercase">{artwork.license_type}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link
                to="/shop"
                className="inline-block bg-teal-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                View All Artworks
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-teal-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Start Creating?</h2>
          <p className="text-xl mb-8 text-teal-100">
            Join thousands of designers who trust Akash Graphic for premium digital resources
          </p>
          <Link
            to="/shop"
            className="inline-block bg-white text-teal-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            Browse Collection
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
